import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Plus, Edit2, Trash2, ExternalLink, GraduationCap, Eye, EyeOff, Lock, BookOpen, Video, FileText, ArrowLeft } from "lucide-react";
import ImageUploader from "../ImageUploader";
import StripeConnectCard from "../StripeConnectCard";
import Brand from "../../components/Brand";

export default function MyCoursesTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | "new" | course
  const [editingLessons, setEditingLessons] = useState(null); // null | course

  const load = () => {
    setLoading(true);
    api.get("/me/seller-courses")
      .then(r => setItems(r.data.courses || []))
      .catch(() => toast.error("Couldn't load your courses"))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const isPaid = user && (user.tier || "free").toLowerCase() !== "free";

  if (user && !isPaid) {
    return (
      <div className="space-y-5" data-testid="my-courses-locked">
        <div className="rounded-2xl bg-gradient-to-br from-[#0F172A] to-[#2A2521] text-white p-8 md:p-10 shadow-2xl border-2 border-[#F39A1F]/40 max-w-3xl mx-auto">
          <div className="flex flex-col items-center text-center gap-5">
            <Lock className="w-14 h-14 text-[#F39A1F]"/>
            <div>
              <div className="text-[10px] tracking-wider-cap font-bold text-[#F39A1F]">★ ALL ACCESS MEMBERS ONLY</div>
              <h3 className="font-serif-display text-3xl md:text-4xl text-white uppercase mt-1 leading-tight text-balance">Build & Sell Your Own Courses. Keep 100%.</h3>
              <p className="text-white/85 mt-3 text-base max-w-xl mx-auto text-balance">All Access members can build their own video + lesson courses, set their own price, and sell them on /u/your-name. <Brand/> takes ZERO cut — your $49/mo IS the platform fee.</p>
              <Link to="/pricing?plan=faithful_monthly" className="mt-5 inline-block bg-[#F39A1F] text-[#0F172A] px-6 py-3 rounded-md font-bold uppercase tracking-wider-cap text-xs hover:bg-white transition shadow-lg">★ Get All Access · $49/mo</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (editingLessons) {
    return <LessonManager course={editingLessons} onBack={() => { setEditingLessons(null); load(); }}/>;
  }

  return (
    <div className="space-y-6" data-testid="my-courses-tab">
      <div>
        <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">★ MY COURSES</div>
        <h2 className="text-3xl uppercase text-red-wine">Build A Course. Sell It Forever.</h2>
        <p className="text-foreground/85 mt-2 max-w-2xl text-base">Create video + lesson courses. Set your own price. We take <strong className="text-red-wine">0%</strong>. Buyers find them at <code className="bg-foreground/5 px-1.5 py-0.5 rounded text-xs">/u/{user?.username || "your-name"}/store</code></p>
      </div>

      <StripeConnectCard />

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-foreground/70">{items.length} {items.length === 1 ? "course" : "courses"} · <Link to={`/u/${user?.username}/store`} target="_blank" rel="noopener noreferrer" className="text-red-wine font-bold hover:underline inline-flex items-center gap-1">View public storefront <ExternalLink className="w-3 h-3"/></Link></div>
        <button onClick={() => setEditing("new")} data-testid="add-course-btn" className="bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-4 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs inline-flex items-center gap-2 shadow"><Plus className="w-4 h-4"/> Add Course</button>
      </div>

      {loading ? (
        <div className="text-foreground/60 text-center py-10">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 rounded-xl bg-white/60 border-2 border-dashed border-[#F39A1F]/40" data-testid="my-courses-empty">
          <GraduationCap className="w-10 h-10 text-[#F39A1F] mx-auto mb-2"/>
          <div className="font-serif-display text-2xl text-red-wine/80 uppercase">No courses yet.</div>
          <p className="text-sm text-foreground/70 mt-1 mb-4">Build your first course — your story, your teaching, your gift.</p>
          <button onClick={() => setEditing("new")} className="bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-5 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs inline-flex items-center gap-2"><Plus className="w-4 h-4"/> Add Your First Course</button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-courses-grid">
          {items.map(c => <CourseCard key={c.id} course={c} onEdit={() => setEditing(c)} onLessons={() => setEditingLessons(c)} onChange={load}/>)}
        </div>
      )}

      {editing && <CourseEditor course={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function CourseCard({ course, onEdit, onLessons, onChange }) {
  const togglePublish = async () => {
    const newStatus = course.status === "published" ? "draft" : "published";
    try {
      await api.patch(`/me/seller-courses/${course.id}`, { status: newStatus });
      toast.success(newStatus === "published" ? "Now published" : "Unpublished");
      onChange();
    } catch (e) { toast.error("Couldn't update"); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${course.title}"?`)) return;
    try {
      await api.delete(`/me/seller-courses/${course.id}`);
      toast.success("Deleted");
      onChange();
    } catch (e) { toast.error("Couldn't delete"); }
  };
  const isPub = course.status === "published";
  const lessonCount = (course.lessons || []).length;
  return (
    <div className="bg-white rounded-xl border-2 border-foreground/10 hover:border-[#F39A1F] hover:shadow-xl transition flex flex-col overflow-hidden" data-testid={`my-course-${course.id}`}>
      <div className="aspect-[16/9] bg-foreground/5 relative overflow-hidden">
        {course.thumbnail ? <img src={course.thumbnail} alt="" className="w-full h-full object-contain p-3 bg-white" loading="lazy"/> : <div className="w-full h-full flex items-center justify-center"><GraduationCap className="w-12 h-12 text-foreground/20"/></div>}
        <div className={`absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider-cap px-2 py-0.5 rounded-full ${isPub ? "bg-[#22693C] text-white" : "bg-foreground/30 text-white"}`}>{isPub ? "★ PUBLISHED" : "DRAFT"}</div>
        <div className="absolute top-2 right-2 bg-[#F39A1F] text-[#0F172A] text-xs font-bold px-2.5 py-0.5 rounded-full">{course.price_usd > 0 ? `$${course.price_usd.toFixed(2)}` : "FREE"}</div>
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-bold text-red-wine text-base leading-tight mb-1 line-clamp-2">{course.title}</div>
        <div className="text-xs text-foreground/65 line-clamp-2 mb-2">{course.description}</div>
        <div className="text-[10px] tracking-wider-cap font-bold text-foreground/50 mb-3">{lessonCount} {lessonCount === 1 ? "LESSON" : "LESSONS"}</div>
        <div className="mt-auto grid grid-cols-2 gap-1.5 pt-2 border-t border-foreground/10">
          <button onClick={onLessons} data-testid={`manage-lessons-${course.id}`} className="col-span-2 bg-white hover:bg-[#FBE9A0] text-[#2E9F3A] py-2 rounded-md text-xs uppercase tracking-wider-cap font-bold inline-flex items-center justify-center gap-1.5 border border-[#F39A1F]/40"><BookOpen className="w-3.5 h-3.5"/> Manage Lessons ({lessonCount})</button>
          <button onClick={onEdit} data-testid={`edit-course-${course.id}`} className="inline-flex items-center justify-center gap-1 text-xs uppercase tracking-wider-cap font-bold text-foreground hover:text-red-wine py-2 px-2 rounded-md hover:bg-foreground/5"><Edit2 className="w-3 h-3"/> Edit</button>
          <button onClick={togglePublish} data-testid={`toggle-course-${course.id}`} className="inline-flex items-center justify-center gap-1 text-xs uppercase tracking-wider-cap font-bold text-foreground hover:text-red-wine py-2 px-2 rounded-md hover:bg-foreground/5">{isPub ? <><EyeOff className="w-3 h-3"/> Hide</> : <><Eye className="w-3 h-3"/> Publish</>}</button>
          <button onClick={remove} data-testid={`delete-course-${course.id}`} className="col-span-2 inline-flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider-cap font-bold text-foreground/55 hover:text-red-wine py-1 rounded-md hover:bg-foreground/5"><Trash2 className="w-3 h-3"/> Delete Course</button>
        </div>
      </div>
    </div>
  );
}

function CourseEditor({ course, onClose, onSaved }) {
  const [form, setForm] = useState(course ? {
    title: course.title || "", description: course.description || "", price_usd: course.price_usd || 0,
    thumbnail: course.thumbnail || "", purchase_link: course.purchase_link || "", status: course.status || "draft",
  } : { title: "", description: "", price_usd: 0, thumbnail: "", purchase_link: "", status: "draft" });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, price_usd: parseFloat(form.price_usd) || 0 };
      if (course) await api.patch(`/me/seller-courses/${course.id}`, payload);
      else await api.post("/me/seller-courses", payload);
      toast.success(course ? "Saved" : "Course created — now add lessons");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-onyx/70 z-50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={onClose} data-testid="course-editor-modal">
      <div className="bg-white rounded-t-2xl md:rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto shadow-2xl border-t-4 border-[#F39A1F]" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-[#0F172A] to-[#2A2521] text-white px-5 py-4 flex items-center justify-between sticky top-0 z-10">
          <div>
            <div className="text-[10px] tracking-wider-cap font-bold text-[#F39A1F]">★ {course ? "EDIT COURSE" : "NEW COURSE"}</div>
            <div className="font-serif-display text-2xl">{course ? form.title || "Untitled" : "Add Course"}</div>
          </div>
          <button onClick={onClose} data-testid="course-editor-close" className="text-white/80 hover:text-white p-1.5 hover:bg-white/10 rounded">✕</button>
        </div>
        <form onSubmit={save} className="p-5 space-y-4">
          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Course Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} data-testid="course-title" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm" placeholder="e.g. The 7-Day Faith Reset" maxLength={200}/>
          </div>
          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Description</label>
            <textarea rows={4} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} data-testid="course-description" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm" placeholder="Who it's for. What they'll learn. How long it takes."/>
          </div>
          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Price (USD) · <span className="text-foreground/55 normal-case font-normal">use 0 for a free preview course</span></label>
            <input type="number" step="0.01" min="0" value={form.price_usd} onChange={e => setForm(f => ({...f, price_usd: e.target.value}))} data-testid="course-price" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm" placeholder="29.00"/>
          </div>
          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Thumbnail Image</label>
            <ImageUploader value={form.thumbnail} onChange={url => setForm(f => ({...f, thumbnail: url}))} testid="course-thumb"/>
          </div>
          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Payment Link · <span className="text-foreground/55 normal-case font-normal">PayPal / Cash App / Square / Stripe — until our Connect goes live</span></label>
            <input value={form.purchase_link} onChange={e => setForm(f => ({...f, purchase_link: e.target.value}))} data-testid="course-purchase-link" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm font-mono" placeholder="https://paypal.me/yourname/29"/>
          </div>
          <div>
            <label className="text-[10px] tracking-wider-cap font-bold text-gold-deep block mb-1">Visibility</label>
            <div className="flex gap-2">
              {["draft", "published"].map(s => (
                <button key={s} type="button" onClick={() => setForm(f => ({...f, status: s}))} data-testid={`course-status-${s}`} className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider-cap font-bold border-2 transition ${form.status === s ? (s === "published" ? "bg-[#22693C] text-white border-[#22693C]" : "bg-foreground/40 text-white border-foreground/40") : "bg-white text-foreground border-foreground/20 hover:border-foreground/40"}`}>{s === "published" ? "★ Published" : "Draft"}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2 sticky bottom-0 bg-white -mx-5 px-5 pb-2 border-t border-foreground/10">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-3 rounded-md font-bold uppercase tracking-wider-cap text-xs border-2 border-foreground/20 text-foreground hover:bg-foreground/5">Cancel</button>
            <button type="submit" disabled={saving} data-testid="course-save-btn" className="flex-1 bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-4 py-3 rounded-md font-bold uppercase tracking-wider-cap text-xs disabled:opacity-50">{saving ? "Saving…" : (course ? "Save Changes" : "★ Create Course")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ============== LESSON MANAGER ============== */
function LessonManager({ course, onBack }) {
  const [lessons, setLessons] = useState(course.lessons || []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", video_url: "", content: "" });
  const [saving, setSaving] = useState(false);

  const addLesson = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/me/seller-courses/${course.id}/lessons`, form);
      setLessons([...lessons, r.data]);
      setForm({ title: "", description: "", video_url: "", content: "" });
      setAdding(false);
      toast.success("Lesson added");
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't add"); }
    finally { setSaving(false); }
  };

  const removeLesson = async (lid) => {
    if (!window.confirm("Delete this lesson?")) return;
    try {
      await api.delete(`/me/seller-courses/${course.id}/lessons/${lid}`);
      setLessons(lessons.filter(l => l.id !== lid));
      toast.success("Lesson removed");
    } catch { toast.error("Couldn't delete"); }
  };

  return (
    <div className="space-y-5" data-testid="lesson-manager">
      <button onClick={onBack} className="text-xs uppercase tracking-wider-cap font-bold text-foreground/60 hover:text-red-wine inline-flex items-center gap-1.5"><ArrowLeft className="w-3.5 h-3.5"/> Back to my courses</button>
      <div>
        <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">★ COURSE · {course.title}</div>
        <h2 className="text-3xl uppercase text-red-wine">Lessons</h2>
      </div>

      {lessons.length === 0 ? (
        <div className="text-center py-10 rounded-xl bg-white/60 border-2 border-dashed border-[#F39A1F]/40">
          <FileText className="w-10 h-10 text-[#F39A1F] mx-auto mb-2"/>
          <div className="font-serif-display text-xl text-red-wine/80 uppercase">No lessons yet.</div>
          <p className="text-sm text-foreground/70 mt-1">Add your first lesson below — text, a YouTube video, or both.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="lesson-list">
          {lessons.map((l, i) => (
            <div key={l.id} className="bg-white rounded-lg border-2 border-foreground/10 p-4 flex items-start gap-3" data-testid={`lesson-${l.id}`}>
              <div className="bg-white text-[#2E9F3A] font-serif-display text-2xl w-12 h-12 rounded-md flex items-center justify-center shrink-0 border-2 border-[#F39A1F]/40">{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-red-wine">{l.title}</div>
                {l.description && <div className="text-xs text-foreground/65 mt-0.5 line-clamp-2">{l.description}</div>}
                <div className="flex items-center gap-3 mt-1.5 text-[10px] tracking-wider-cap font-bold text-foreground/50">
                  {l.video_url && <span className="inline-flex items-center gap-1"><Video className="w-3 h-3"/> VIDEO</span>}
                  {l.content && <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3"/> TEXT</span>}
                </div>
              </div>
              <button onClick={() => removeLesson(l.id)} data-testid={`delete-lesson-${l.id}`} className="text-foreground/40 hover:text-red-wine p-1.5 rounded hover:bg-foreground/5"><Trash2 className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <form onSubmit={addLesson} className="bg-white rounded-xl border-2 border-[#F39A1F] p-5 space-y-3" data-testid="add-lesson-form">
          <div className="text-[10px] tracking-wider-cap font-bold text-gold-deep">★ NEW LESSON</div>
          <input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Lesson title" data-testid="lesson-title" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm" maxLength={200}/>
          <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Short description (optional)" data-testid="lesson-description" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm" maxLength={2000}/>
          <input value={form.video_url} onChange={e => setForm(f => ({...f, video_url: e.target.value}))} placeholder="YouTube / Vimeo / MP4 URL (optional)" data-testid="lesson-video" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm font-mono"/>
          <textarea rows={5} value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))} placeholder="Lesson body / transcript / notes (optional)" data-testid="lesson-content" className="w-full px-3 py-2.5 rounded-md border-2 border-foreground/20 focus:border-[#F39A1F] text-sm"/>
          <div className="flex gap-2">
            <button type="button" onClick={() => setAdding(false)} className="flex-1 px-4 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs border-2 border-foreground/20 text-foreground hover:bg-foreground/5">Cancel</button>
            <button type="submit" disabled={saving} data-testid="lesson-save-btn" className="flex-1 bg-[#2E9F3A] hover:bg-[#1F8A2A] text-white px-4 py-2.5 rounded-md font-bold uppercase tracking-wider-cap text-xs disabled:opacity-50">{saving ? "Adding…" : "★ Add Lesson"}</button>
          </div>
        </form>
      ) : (
        <button onClick={() => setAdding(true)} data-testid="show-add-lesson" className="w-full bg-white hover:bg-[#FBE9A0] border-2 border-dashed border-[#F39A1F] text-[#2E9F3A] py-3 rounded-md font-bold uppercase tracking-wider-cap text-xs inline-flex items-center justify-center gap-2"><Plus className="w-4 h-4"/> Add Lesson</button>
      )}
    </div>
  );
}
